/**
 * Componente AnimatedSection
 * Sección con animaciones de entrada controladas por scroll
 * 
 * Uso: Envolver cualquier contenido para animarlo al entrar en el viewport
 * Incluye múltiples variantes de animación inspiradas en sitios Awwwards
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Sección animada que revela su contenido al hacer scroll
 * 
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Contenido a animar
 * @param {string} props.animation - Tipo de animación ('fadeUp', 'fadeIn', 'slideLeft', 'slideRight', 'scale', 'reveal')
 * @param {number} props.delay - Retraso antes de iniciar la animación (en segundos)
 * @param {number} props.duration - Duración de la animación (en segundos)
 * @param {string} props.className - Clases CSS adicionales
 * @param {boolean} props.stagger - Si los hijos deben animarse en secuencia
 * @param {string} props.triggerStart - Posición de inicio del trigger
 * @param {boolean} props.scrub - Si la animación debe vincularse al scroll
 * @returns {JSX.Element} Sección animada
 */
const AnimatedSection = ({
  children,
  animation = 'fadeUp',
  delay = 0,
  duration = 1,
  className = '',
  stagger = false,
  triggerStart = 'top 85%',
  scrub = false,
}) => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const content = contentRef.current;

    if (!section || !content) return;

    // Configuración base de la animación según el tipo
    const animations = {
      fadeUp: {
        from: { y: 80, opacity: 0 },
        to: { y: 0, opacity: 1 },
      },
      fadeIn: {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      slideLeft: {
        from: { x: 100, opacity: 0 },
        to: { x: 0, opacity: 1 },
      },
      slideRight: {
        from: { x: -100, opacity: 0 },
        to: { x: 0, opacity: 1 },
      },
      scale: {
        from: { scale: 0.8, opacity: 0 },
        to: { scale: 1, opacity: 1 },
      },
      reveal: {
        from: { clipPath: 'inset(100% 0 0 0)' },
        to: { clipPath: 'inset(0% 0 0 0)' },
      },
      blur: {
        from: { filter: 'blur(20px)', opacity: 0 },
        to: { filter: 'blur(0px)', opacity: 1 },
      },
      rotate: {
        from: { rotation: 10, y: 50, opacity: 0 },
        to: { rotation: 0, y: 0, opacity: 1 },
      },
    };

    const selectedAnimation = animations[animation] || animations.fadeUp;
    const targets = stagger ? content.children : content;

    // Establecer estado inicial
    gsap.set(targets, selectedAnimation.from);

    // Crear animación con ScrollTrigger
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: triggerStart,
        end: scrub ? 'bottom center' : undefined,
        scrub: scrub ? 1 : false,
        toggleActions: scrub ? undefined : 'play none none reverse',
      },
    });

    tl.to(targets, {
      ...selectedAnimation.to,
      duration: scrub ? undefined : duration,
      delay: scrub ? undefined : delay,
      ease: 'power3.out',
      stagger: stagger ? 0.15 : 0,
    });

    // Cleanup
    return () => {
      tl.kill();
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === section) {
          trigger.kill();
        }
      });
    };
  }, [animation, delay, duration, stagger, triggerStart, scrub]);

  return (
    <section ref={sectionRef} className={className}>
      <div ref={contentRef} className="h-full">
        {children}
      </div>
    </section>
  );
};

AnimatedSection.propTypes = {
  children: PropTypes.node.isRequired,
  animation: PropTypes.oneOf([
    'fadeUp',
    'fadeIn',
    'slideLeft',
    'slideRight',
    'scale',
    'reveal',
    'blur',
    'rotate',
  ]),
  delay: PropTypes.number,
  duration: PropTypes.number,
  className: PropTypes.string,
  stagger: PropTypes.bool,
  triggerStart: PropTypes.string,
  scrub: PropTypes.bool,
};

export default AnimatedSection;
